import { z } from "zod"

import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import {
  correlationIdSchema,
  platformContextIdSchema,
  platformOperatorIdSchema,
  platformSessionIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const dnsNameSchema = z
  .string()
  .min(3)
  .max(253)
  .regex(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  )
const versionSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9._+-]+$/)

export const tenantServiceLifecycleStateV1Schema = z.enum([
  "pending",
  "reconciling",
  "blocked",
  "ready",
])
export const tenantDomainStateV1Schema = z.enum([
  "unknown",
  "pending",
  "verified",
  "failed",
])
export const tenantCertificateStateV1Schema = z.enum([
  "unknown",
  "pending",
  "ready",
  "expired",
  "failed",
])
export const tenantBrandStateV1Schema = z.enum([
  "unknown",
  "pending",
  "ready",
  "failed",
])
export const tenantConfigurationMatchV1Schema = z.enum([
  "unknown",
  "matches",
  "mismatch",
])
export const tenantVersionCompatibilityV1Schema = z.enum([
  "unknown",
  "compatible",
  "incompatible",
])
export const tenantServiceRollbackBlockerV1Schema = z.enum([
  "DOMAIN_UNVERIFIED",
  "CERTIFICATE_NOT_READY",
  "BRAND_NOT_READY",
  "REDIRECT_MISMATCH",
  "RECOVERY_FLOW_MISMATCH",
  "APP_VERSION_INCOMPATIBLE",
  "SCHEMA_VERSION_INCOMPATIBLE",
  "ROLLBACK_APP_UNAVAILABLE",
  "ROLLBACK_SCHEMA_UNAVAILABLE",
  "PROVIDER_UNAVAILABLE",
])
export const tenantServiceFailureCodeV1Schema = z.enum([
  "ROUTE_NOT_READY",
  "DOMAIN_OWNERSHIP_UNVERIFIED",
  "CERTIFICATE_NOT_READY",
  "BRAND_CONFIGURATION_FAILED",
  "REDIRECT_CONFIGURATION_MISMATCH",
  "RECOVERY_FLOW_MISMATCH",
  "APP_VERSION_INCOMPATIBLE",
  "SCHEMA_VERSION_INCOMPATIBLE",
  "ROLLBACK_VERSION_UNAVAILABLE",
  "PROVIDER_UNAVAILABLE",
])

export const tenantServiceReadinessV1Schema = z
  .object({
    domainState: tenantDomainStateV1Schema,
    domainVerifiedAt: z.string().datetime({ offset: true }).nullable(),
    certificateState: tenantCertificateStateV1Schema,
    certificateExpiresAt: z.string().datetime({ offset: true }).nullable(),
    brandState: tenantBrandStateV1Schema,
    redirectConfiguration: tenantConfigurationMatchV1Schema,
    recoveryConfiguration: tenantConfigurationMatchV1Schema,
    observedAppVersion: versionSchema.nullable(),
    desiredAppVersion: versionSchema,
    observedSchemaVersion: versionSchema.nullable(),
    desiredSchemaVersion: versionSchema,
    compatibility: tenantVersionCompatibilityV1Schema,
    rollbackAppVersion: versionSchema,
    rollbackSchemaVersion: versionSchema,
    rollbackBlockers: z
      .array(tenantServiceRollbackBlockerV1Schema)
      .max(10)
      .refine((values) => new Set(values).size === values.length, {
        message: "Rollback blockers must be unique.",
      }),
  })
  .strict()

export const tenantServiceLifecycleSnapshotV1Schema = z
  .object({
    contractVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    applicationHostname: dnsNameSchema,
    authenticationDomain: dnsNameSchema,
    siteName: dnsNameSchema,
    state: tenantServiceLifecycleStateV1Schema,
    failureCode: tenantServiceFailureCodeV1Schema.nullable(),
    attempt: z.number().int().positive().safe(),
    revision: z.number().int().positive().safe(),
    readiness: tenantServiceReadinessV1Schema,
    readyAt: z.string().datetime({ offset: true }).nullable(),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((snapshot, context) => {
    const readiness = snapshot.readiness
    const ready =
      readiness.domainState === "verified"
      && readiness.domainVerifiedAt !== null
      && readiness.certificateState === "ready"
      && readiness.certificateExpiresAt !== null
      && readiness.brandState === "ready"
      && readiness.redirectConfiguration === "matches"
      && readiness.recoveryConfiguration === "matches"
      && readiness.observedAppVersion === readiness.desiredAppVersion
      && readiness.observedSchemaVersion === readiness.desiredSchemaVersion
      && readiness.compatibility === "compatible"
      && readiness.rollbackBlockers.length === 0
    if (snapshot.state === "ready" && (!ready || snapshot.readyAt === null)) {
      context.addIssue({
        code: "custom",
        message: "Ready service lifecycle requires every authoritative gate.",
      })
    }
    if (snapshot.state !== "ready" && snapshot.readyAt !== null) {
      context.addIssue({
        code: "custom",
        path: ["readyAt"],
        message: "Only a ready service lifecycle may have a ready timestamp.",
      })
    }
  })

export const tenantServiceLifecycleCommandV1Schema = z
  .object({
    envelope: platformCommandEnvelopeV1Schema,
    request: z
      .object({
        targetTenantId: tenantIdSchema,
        authenticationDomain: dnsNameSchema,
        desiredAppVersion: versionSchema,
        desiredSchemaVersion: versionSchema,
        rollbackAppVersion: versionSchema,
        rollbackSchemaVersion: versionSchema,
      })
      .strict(),
  })
  .strict()
  .superRefine((command, context) => {
    const target = command.envelope.target
    const request = command.request
    const payload = command.envelope.payload
    if (
      target.scope !== "tenant"
      || target.targetTenantId !== request.targetTenantId
      || String(target.targetId) !== request.targetTenantId
      || target.targetType !== "tenant_service_lifecycle"
      || command.envelope.idempotencyNamespace !== "tenant.service.reconcile"
      || command.envelope.commandType !== "ReconcileTenantServiceLifecycle"
      || payload.targetTenantId !== request.targetTenantId
      || payload.authenticationDomain !== request.authenticationDomain
      || payload.desiredAppVersion !== request.desiredAppVersion
      || payload.desiredSchemaVersion !== request.desiredSchemaVersion
      || payload.rollbackAppVersion !== request.rollbackAppVersion
      || payload.rollbackSchemaVersion !== request.rollbackSchemaVersion
    ) {
      context.addIssue({
        code: "custom",
        message: "Service lifecycle command does not match its exact Tenant target.",
      })
    }
  })

export const tenantServiceLifecycleResponseV1Schema = z.discriminatedUnion(
  "ok",
  [
    z
      .object({
        responseVersion: z.literal(1),
        ok: z.literal(true),
        replayed: z.boolean(),
        snapshot: tenantServiceLifecycleSnapshotV1Schema,
      })
      .strict(),
    z
      .object({
        responseVersion: z.literal(1),
        ok: z.literal(false),
        error: z
          .object({
            code: z.enum([
              "INVALID_REQUEST",
              "DENIED",
              "NOT_FOUND",
              "BUSY",
              "CONFLICT",
              "VERSION_CONFLICT",
              "RECONCILIATION_BLOCKED",
              "UNAVAILABLE",
            ]),
            retryable: z.boolean(),
            message: z.string().min(1).max(128),
          })
          .strict(),
        current: tenantServiceLifecycleSnapshotV1Schema.optional(),
      })
      .strict(),
  ],
)

export const tenantServiceLifecycleQueryV1Schema = z
  .object({
    envelope: z
      .object({
        contractVersion: z.literal(1),
        platformContextId: platformContextIdSchema,
        platformOperatorId: platformOperatorIdSchema,
        platformSessionId: platformSessionIdSchema,
        correlationId: correlationIdSchema,
        queryType: z.literal("TenantServiceLifecycle"),
        queryVersion: z.literal(1),
      })
      .strict(),
    targetTenantId: tenantIdSchema,
  })
  .strict()

export const tenantServiceAttemptV1Schema = z
  .object({
    attempt: z.number().int().positive().safe(),
    revision: z.number().int().positive().safe(),
    state: z.enum(["started", "ready", "blocked"]),
    failureCode: tenantServiceFailureCodeV1Schema.nullable(),
    occurredAt: z.string().datetime({ offset: true }),
  })
  .strict()

export const tenantServiceLifecycleWorkspaceV1Schema = z
  .object({
    responseVersion: z.literal(1),
    targetTenantId: tenantIdSchema,
    snapshot: tenantServiceLifecycleSnapshotV1Schema.nullable(),
    attempts: z.array(tenantServiceAttemptV1Schema).max(25),
  })
  .strict()

export type TenantServiceFailureCodeV1 = z.infer<
  typeof tenantServiceFailureCodeV1Schema
>
export type TenantServiceLifecycleCommandV1 = z.infer<
  typeof tenantServiceLifecycleCommandV1Schema
>
export type TenantServiceLifecycleQueryV1 = z.infer<
  typeof tenantServiceLifecycleQueryV1Schema
>
export type TenantServiceLifecycleResponseV1 = z.infer<
  typeof tenantServiceLifecycleResponseV1Schema
>
export type TenantServiceLifecycleSnapshotV1 = z.infer<
  typeof tenantServiceLifecycleSnapshotV1Schema
>
export type TenantServiceLifecycleWorkspaceV1 = z.infer<
  typeof tenantServiceLifecycleWorkspaceV1Schema
>
export type TenantServiceReadinessV1 = z.infer<
  typeof tenantServiceReadinessV1Schema
>
export type TenantServiceRollbackBlockerV1 = z.infer<
  typeof tenantServiceRollbackBlockerV1Schema
>
